import { motion } from "framer-motion";
import { Plane, Clock, ArrowRight } from "lucide-react";
import AnimatedSection from "./AnimatedSection";

const flights = [
  {
    type: "Outbound",
    airline: "TAP Portugal",
    flightNo: "TP 258",
    travelers: "Aarin Jasikumar & Jade Ally",
    departure: { city: "Toronto", code: "YYZ", time: "5:30 PM", date: "May 17" },
    arrival: { city: "Lisbon", code: "LIS", time: "5:50 AM", date: "May 18" },
    duration: "7h 20m",
    direct: true,
  },
  {
    type: "Outbound",
    airline: "Porter / Air Transat",
    flightNo: "PD125 / TS680",
    travelers: "Jordan Ally, Avery Chan, Shameer Ally, Channa Say-Ally",
    departure: { city: "Toronto", code: "YYZ", time: "3:40 PM", date: "May 17" },
    arrival: { city: "Lisbon", code: "LIS", time: "6:30 AM", date: "May 18" },
    duration: "Total: 9h 50m",
    direct: false,
    segments: [
      {
        airline: "Porter",
        flightNo: "PD125",
        departure: { city: "Toronto", code: "YYZ", time: "3:40 PM", date: "May 17" },
        arrival: { city: "Montreal", code: "YUL", time: "5:02 PM", date: "May 17" },
        duration: "1h 22m",
      },
      {
        airline: "Air Transat",
        flightNo: "TS680",
        departure: { city: "Montreal", code: "YUL", time: "7:00 PM", date: "May 17" },
        arrival: { city: "Lisbon", code: "LIS", time: "6:30 AM", date: "May 18" },
        duration: "6h 30m",
      },
    ],
    layover: "1h 58m at YUL",
  },
  {
    type: "Return",
    airline: "TAP Portugal",
    flightNo: "TP 257",
    travelers: "Aarin Jasikumar & Jade Ally",
    departure: { city: "Lisbon", code: "LIS", time: "10:40 AM", date: "May 27" },
    arrival: { city: "Toronto", code: "YYZ", time: "2:00 PM", date: "May 27" },
    duration: "8h 20m",
    direct: true,
  },
  {
    type: "Return",
    airline: "Air Transat / Porter",
    flightNo: "TS681 / TS7820",
    travelers: "Jordan Ally, Avery Chan, Shameer Ally, Channa Say-Ally",
    departure: { city: "Lisbon", code: "LIS", time: "8:15 AM", date: "May 27" },
    arrival: { city: "Toronto", code: "YYZ", time: "4:38 PM", date: "May 27" },
    duration: "Total: 13h 23m",
    direct: false,
    segments: [
      {
        airline: "Air Transat",
        flightNo: "TS681",
        departure: { city: "Lisbon", code: "LIS", time: "8:15 AM", date: "May 27" },
        arrival: { city: "Montreal", code: "YUL", time: "10:40 AM", date: "May 27" },
        duration: "7h 25m",
      },
      {
        airline: "Porter",
        flightNo: "TS7820",
        departure: { city: "Montreal", code: "YUL", time: "3:10 PM", date: "May 27" },
        arrival: { city: "Toronto", code: "YYZ", time: "4:38 PM", date: "May 27" },
        duration: "1h 28m",
      },
    ],
    layover: "4h 30m at YUL",
  },
];

const TravelInfo = () => {
  return (
    <section className="section-padding bg-background">
      <div className="container max-w-6xl mx-auto">
        <AnimatedSection>
          <span className="text-primary font-medium tracking-widest uppercase text-sm">
            Travel Details
          </span>
          <h2 className="text-4xl md:text-5xl font-serif font-bold mt-2 mb-12">
            Flight Information
          </h2>
        </AnimatedSection>

        {/* Direct Flights Section */}
        <div className="mb-8">
          <h3 className="text-2xl font-serif font-semibold mb-6 text-center">Direct Flights</h3>
          <div className="grid md:grid-cols-2 gap-6 lg:gap-8">
            {flights.filter(flight => flight.direct).map((flight, index) => (
              <AnimatedSection key={`${flight.type}-${flight.travelers}-${index}`} delay={index * 0.2}>
                <motion.div
                  whileHover={{ y: -5, scale: 1.01 }}
                  transition={{ duration: 0.3 }}
                  className="glass-card rounded-2xl p-6 md:p-8 hover:shadow-lg transition-all duration-300"
                >
                <div className="flex items-center justify-between mb-6 md:mb-8">
                  <div className="flex items-center gap-3">
                    <motion.div
                      whileHover={{ rotate: 15, scale: 1.1 }}
                      transition={{ duration: 0.2 }}
                      className="p-2.5 rounded-lg bg-primary/10"
                    >
                      <Plane className="w-5 h-5 text-primary" />
                    </motion.div>
                    <div>
                      <p className="font-semibold text-base md:text-lg">{flight.airline}</p>
                      <p className="text-sm text-muted-foreground">{flight.flightNo}</p>
                      {flight.travelers && (
                        <p className="text-xs text-muted-foreground mt-1">{flight.travelers}</p>
                      )}
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-2">
                    <span className="px-3 py-1.5 rounded-full bg-ocean-light text-ocean text-sm font-medium whitespace-nowrap">
                      {flight.type}
                    </span>
                    {flight.direct ? (
                      <span className="px-2 py-1 rounded-full bg-green-100 text-green-700 text-xs font-medium">
                        Direct
                      </span>
                    ) : (
                      <span className="px-2 py-1 rounded-full bg-orange-100 text-orange-700 text-xs font-medium">
                        Connecting
                      </span>
                    )}
                  </div>
                </div>

                {flight.segments ? (
                  // Multi-segment flight
                  <div className="space-y-4">
                    {flight.segments.map((segment, segIndex) => (
                      <div key={segIndex}>
                        <div className="flex items-center justify-between gap-2 md:gap-4 mb-2">
                          <div className="text-center flex-1 min-w-0">
                            <p className="text-xl md:text-2xl font-serif font-bold">{segment.departure.code}</p>
                            <p className="text-xs text-muted-foreground mt-1 truncate">{segment.departure.city}</p>
                            <p className="font-medium mt-1 text-sm">{segment.departure.time}</p>
                            <p className="text-xs text-muted-foreground">{segment.departure.date}</p>
                          </div>

                          <div className="flex-1 mx-2 md:mx-4 min-w-[60px]">
                            <div className="relative flex items-center justify-center">
                              <div className="w-full h-px bg-border" />
                              <motion.div
                                animate={{ x: [0, 10, 0] }}
                                transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
                                className="absolute bg-card border border-border/50 rounded-full p-1 shadow-sm"
                              >
                                <ArrowRight className="w-3 h-3 md:w-4 md:h-4 text-primary" />
                              </motion.div>
                            </div>
                            <div className="flex items-center justify-center gap-1 mt-2 text-xs text-muted-foreground">
                              <Clock className="w-3 h-3" />
                              <span className="whitespace-nowrap">{segment.duration}</span>
                            </div>
                          </div>

                          <div className="text-center flex-1 min-w-0">
                            <p className="text-xl md:text-2xl font-serif font-bold">{segment.arrival.code}</p>
                            <p className="text-xs text-muted-foreground mt-1 truncate">{segment.arrival.city}</p>
                            <p className="font-medium mt-1 text-sm">{segment.arrival.time}</p>
                            <p className="text-xs text-muted-foreground">{segment.arrival.date}</p>
                          </div>
                        </div>
                        <div className="text-center mb-2">
                          <p className="text-xs text-muted-foreground">{segment.airline} {segment.flightNo}</p>
                        </div>
                        {segIndex < flight.segments.length - 1 && flight.layover && (
                          <div className="bg-muted/50 rounded-lg p-2 text-center mb-2">
                            <p className="text-xs text-muted-foreground">Layover: {flight.layover}</p>
                          </div>
                        )}
                      </div>
                    ))}
                    <div className="text-center pt-2 border-t border-border">
                      <div className="flex items-center justify-center gap-1 text-sm text-muted-foreground">
                        <Clock className="w-4 h-4" />
                        <span>{flight.duration}</span>
                      </div>
                    </div>
                  </div>
                ) : (
                  // Direct flight
                  <div className="flex items-center justify-between gap-2 md:gap-4">
                    <div className="text-center flex-1 min-w-0">
                      <p className="text-2xl md:text-3xl font-serif font-bold">{flight.departure.code}</p>
                      <p className="text-xs md:text-sm text-muted-foreground mt-1 truncate">{flight.departure.city}</p>
                      <p className="font-medium mt-2 text-sm md:text-base">{flight.departure.time}</p>
                      <p className="text-xs md:text-sm text-muted-foreground">{flight.departure.date}</p>
                    </div>

                    <div className="flex-1 mx-2 md:mx-4 min-w-[80px]">
                      <div className="relative flex items-center justify-center">
                        <div className="w-full h-px bg-border" />
                        <motion.div
                          animate={{ x: [0, 10, 0] }}
                          transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
                          className="absolute bg-card border border-border/50 rounded-full p-1.5 shadow-sm"
                        >
                          <ArrowRight className="w-4 h-4 md:w-5 md:h-5 text-primary" />
                        </motion.div>
                      </div>
                      <div className="flex items-center justify-center gap-1 mt-3 md:mt-4 text-xs md:text-sm text-muted-foreground">
                        <Clock className="w-3 h-3 md:w-4 md:h-4" />
                        <span className="whitespace-nowrap">{flight.duration}</span>
                      </div>
                    </div>

                    <div className="text-center flex-1 min-w-0">
                      <p className="text-2xl md:text-3xl font-serif font-bold">{flight.arrival.code}</p>
                      <p className="text-xs md:text-sm text-muted-foreground mt-1 truncate">{flight.arrival.city}</p>
                      <p className="font-medium mt-2 text-sm md:text-base">{flight.arrival.time}</p>
                      <p className="text-xs md:text-sm text-muted-foreground">{flight.arrival.date}</p>
                    </div>
                  </div>
                )}
              </motion.div>
            </AnimatedSection>
            ))}
          </div>
        </div>

        {/* Connecting Flights Section */}
        <div>
          <h3 className="text-2xl font-serif font-semibold mb-6 text-center">Connecting Flights</h3>
          <div className="grid md:grid-cols-2 gap-6 lg:gap-8">
            {flights.filter(flight => !flight.direct).map((flight, index) => (
              <AnimatedSection key={`${flight.type}-${flight.travelers}-${index}`} delay={index * 0.2}>
                <motion.div
                  whileHover={{ y: -5, scale: 1.01 }}
                  transition={{ duration: 0.3 }}
                  className="glass-card rounded-2xl p-6 md:p-8 hover:shadow-lg transition-all duration-300"
                >
                  <div className="flex items-center justify-between mb-6 md:mb-8">
                    <div className="flex items-center gap-3">
                      <motion.div
                        whileHover={{ rotate: 15, scale: 1.1 }}
                        transition={{ duration: 0.2 }}
                        className="p-2.5 rounded-lg bg-primary/10"
                      >
                        <Plane className="w-5 h-5 text-primary" />
                      </motion.div>
                      <div>
                        <p className="font-semibold text-base md:text-lg">{flight.airline}</p>
                        <p className="text-sm text-muted-foreground">{flight.flightNo}</p>
                        {flight.travelers && (
                          <p className="text-xs text-muted-foreground mt-1">{flight.travelers}</p>
                        )}
                      </div>
                    </div>
                    <div className="flex flex-col items-end gap-2">
                      <span className="px-3 py-1.5 rounded-full bg-ocean-light text-ocean text-sm font-medium whitespace-nowrap">
                        {flight.type}
                      </span>
                      {flight.direct ? (
                        <span className="px-2 py-1 rounded-full bg-green-100 text-green-700 text-xs font-medium">
                          Direct
                        </span>
                      ) : (
                        <span className="px-2 py-1 rounded-full bg-orange-100 text-orange-700 text-xs font-medium">
                          Connecting
                        </span>
                      )}
                    </div>
                  </div>

                  {flight.segments ? (
                    // Multi-segment flight
                    <div className="space-y-4">
                      {flight.segments.map((segment, segIndex) => (
                        <div key={segIndex}>
                          <div className="flex items-center justify-between gap-2 md:gap-4 mb-2">
                            <div className="text-center flex-1 min-w-0">
                              <p className="text-xl md:text-2xl font-serif font-bold">{segment.departure.code}</p>
                              <p className="text-xs text-muted-foreground mt-1 truncate">{segment.departure.city}</p>
                              <p className="font-medium mt-1 text-sm">{segment.departure.time}</p>
                              <p className="text-xs text-muted-foreground">{segment.departure.date}</p>
                            </div>

                            <div className="flex-1 mx-2 md:mx-4 min-w-[60px]">
                              <div className="relative flex items-center justify-center">
                                <div className="w-full h-px bg-border" />
                                <motion.div
                                  animate={{ x: [0, 10, 0] }}
                                  transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
                                  className="absolute bg-card border border-border/50 rounded-full p-1 shadow-sm"
                                >
                                  <ArrowRight className="w-3 h-3 md:w-4 md:h-4 text-primary" />
                                </motion.div>
                              </div>
                              <div className="flex items-center justify-center gap-1 mt-2 text-xs text-muted-foreground">
                                <Clock className="w-3 h-3" />
                                <span className="whitespace-nowrap">{segment.duration}</span>
                              </div>
                            </div>

                            <div className="text-center flex-1 min-w-0">
                              <p className="text-xl md:text-2xl font-serif font-bold">{segment.arrival.code}</p>
                              <p className="text-xs text-muted-foreground mt-1 truncate">{segment.arrival.city}</p>
                              <p className="font-medium mt-1 text-sm">{segment.arrival.time}</p>
                              <p className="text-xs text-muted-foreground">{segment.arrival.date}</p>
                            </div>
                          </div>
                          <div className="text-center mb-2">
                            <p className="text-xs text-muted-foreground">{segment.airline} {segment.flightNo}</p>
                          </div>
                          {segIndex < flight.segments.length - 1 && flight.layover && (
                            <div className="bg-muted/50 rounded-lg p-2 text-center mb-2">
                              <p className="text-xs text-muted-foreground">Layover: {flight.layover}</p>
                            </div>
                          )}
                        </div>
                      ))}
                      <div className="text-center pt-2 border-t border-border">
                        <div className="flex items-center justify-center gap-1 text-sm text-muted-foreground">
                          <Clock className="w-4 h-4" />
                          <span>{flight.duration}</span>
                        </div>
                      </div>
                    </div>
                  ) : (
                    // Direct flight
                    <div className="flex items-center justify-between gap-2 md:gap-4">
                      <div className="text-center flex-1 min-w-0">
                        <p className="text-2xl md:text-3xl font-serif font-bold">{flight.departure.code}</p>
                        <p className="text-xs md:text-sm text-muted-foreground mt-1 truncate">{flight.departure.city}</p>
                        <p className="font-medium mt-2 text-sm md:text-base">{flight.departure.time}</p>
                        <p className="text-xs md:text-sm text-muted-foreground">{flight.departure.date}</p>
                      </div>

                      <div className="flex-1 mx-2 md:mx-4 min-w-[80px]">
                        <div className="relative flex items-center justify-center">
                          <div className="w-full h-px bg-border" />
                          <motion.div
                            animate={{ x: [0, 10, 0] }}
                            transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
                            className="absolute bg-card border border-border/50 rounded-full p-1.5 shadow-sm"
                          >
                            <ArrowRight className="w-4 h-4 md:w-5 md:h-5 text-primary" />
                          </motion.div>
                        </div>
                        <div className="flex items-center justify-center gap-1 mt-3 md:mt-4 text-xs md:text-sm text-muted-foreground">
                          <Clock className="w-3 h-3 md:w-4 md:h-4" />
                          <span className="whitespace-nowrap">{flight.duration}</span>
                        </div>
                      </div>

                      <div className="text-center flex-1 min-w-0">
                        <p className="text-2xl md:text-3xl font-serif font-bold">{flight.arrival.code}</p>
                        <p className="text-xs md:text-sm text-muted-foreground mt-1 truncate">{flight.arrival.city}</p>
                        <p className="font-medium mt-2 text-sm md:text-base">{flight.arrival.time}</p>
                        <p className="text-xs md:text-sm text-muted-foreground">{flight.arrival.date}</p>
                      </div>
                    </div>
                  )}
                </motion.div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default TravelInfo;